import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useOutletContext } from 'react-router-dom';
import { FolderKanban, Plus, FileText, Files, X, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';

import apiService from '../services/api';
import type { AppShellOutletContext } from '../components/AppShell/AppShell';

/**
 * `/p` — the projects index.
 *
 * A grid of project cards backed by the `GET /projects` data already fetched
 * by AppShell, plus the "פרויקט חדש" dialog (name + optional description)
 * that drives `POST /projects/` and lands the user in the new project's chat.
 */
const ProjectsIndexPage: React.FC = () => {
  const { projects, refreshProjects } = useOutletContext<AppShellOutletContext>();
  const navigate = useNavigate();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);

  const openDialog = () => {
    setName('');
    setDescription('');
    setError(null);
    setDialogOpen(true);
  };
  const closeDialog = () => {
    if (!submitting) setDialogOpen(false);
  };

  // Autofocus the name field when the dialog opens; Escape closes it.
  useEffect(() => {
    if (!dialogOpen) return;
    nameInputRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeDialog();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogOpen]);

  const handleCreate = async (e?: React.FormEvent) => {
    e?.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      setError('יש להזין שם לפרויקט');
      nameInputRef.current?.focus();
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const created = await apiService.createProject(trimmed, description.trim() || undefined);
      toast.success(`הפרויקט "${trimmed}" נוצר`);
      setDialogOpen(false);
      // Refresh the sidebar/index list, then land in the new project's chat —
      // uploads and questions from there are scoped to it automatically.
      await refreshProjects();
      if (created?.id) navigate(`/p/${created.id}/c/new`);
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'יצירת הפרויקט נכשלה. נסה שוב.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex-1 overflow-auto bg-app" dir="rtl">
      <div className="max-w-5xl mx-auto px-6 py-8">
        <header className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">פרויקטים</h1>
            <p className="text-sm text-gray-500 mt-1">
              קבץ מסמכים ושיחות סביב תיק לקוח או סוגיה.
            </p>
          </div>
          <button
            onClick={openDialog}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-500 text-white text-sm font-medium press"
          >
            <Plus className="h-4 w-4" />
            פרויקט חדש
          </button>
        </header>

        {projects.length === 0 ? (
          <div className="bg-white rounded-2xl border border-dashed border-gray-300 p-10 text-center animate-fade-in motion-reduce:animate-none">
            <FolderKanban className="h-10 w-10 text-gray-300 mx-auto mb-3" />
            <h2 className="text-base font-medium text-gray-700 mb-1">אין עדיין פרויקטים</h2>
            <p className="text-sm text-gray-500 mb-4">
              צור פרויקט כדי לקבץ קבצים ושיחות יחד.
            </p>
            <button
              onClick={openDialog}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-500 text-white text-sm font-medium press"
            >
              <Plus className="h-4 w-4" />
              פרויקט חדש
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {projects.map((p, i) => (
              <div
                key={p.id}
                style={{ animationDelay: `${Math.min(i, 8) * 40}ms` }}
                className="group relative bg-white rounded-2xl border border-gray-200 hover:border-primary-300 hover:shadow-md hover:-translate-y-0.5 transition-[transform,border-color,box-shadow] duration-200 ease-out-emil animate-message-in motion-reduce:animate-none"
              >
                <Link
                  to={`/p/${p.id}/c/new`}
                  className="block p-4"
                  title="פתח שיחה על הפרויקט"
                >
                  <div className="flex items-start gap-3">
                    <div className="h-9 w-9 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center flex-shrink-0 group-hover:bg-primary-100 transition-colors">
                      <FolderKanban className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1 pe-8">
                      <h3 className="text-sm font-semibold text-gray-900 truncate">{p.name}</h3>
                      <div className="mt-1 flex items-center gap-1.5 text-xs text-gray-500">
                        <FileText className="h-3 w-3" />
                        <span>{p.document_count} מסמכים</span>
                      </div>
                    </div>
                  </div>
                </Link>
                <Link
                  to={`/p/${p.id}/files`}
                  title="קבצי הפרויקט"
                  aria-label={`קבצי הפרויקט ${p.name}`}
                  className="absolute top-3 left-3 p-1.5 rounded-md text-gray-400 hover:text-primary-600 hover:bg-primary-50 transition-colors"
                >
                  <Files className="h-4 w-4" />
                </Link>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* New-project dialog */}
      {dialogOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="new-project-title"
        >
          <div
            className="absolute inset-0 bg-black/50 animate-fade-in motion-reduce:animate-none"
            onClick={closeDialog}
          />
          <form
            onSubmit={handleCreate}
            className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl p-6 animate-chat-bubble motion-reduce:animate-none"
            dir="rtl"
          >
            <div className="flex items-center justify-between mb-4">
              <h2 id="new-project-title" className="text-lg font-semibold text-gray-900">
                פרויקט חדש
              </h2>
              <button
                type="button"
                onClick={closeDialog}
                disabled={submitting}
                className="p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
                aria-label="סגור"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <label htmlFor="project-name" className="block text-sm font-medium text-gray-700 mb-1">
              שם הפרויקט <span className="text-red-500" aria-hidden="true">*</span>
            </label>
            <input
              id="project-name"
              ref={nameInputRef}
              type="text"
              value={name}
              onChange={(e) => { setName(e.target.value); if (error) setError(null); }}
              placeholder='למשל: "תיק משפחת לוי" או "דוחות טבע 2025"'
              maxLength={120}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-500/15 mb-1"
            />
            {error && (
              <p className="text-xs text-red-600 mb-2" role="alert">{error}</p>
            )}

            <label htmlFor="project-description" className="block text-sm font-medium text-gray-700 mb-1 mt-3">
              תיאור <span className="text-gray-400 font-normal">(לא חובה)</span>
            </label>
            <textarea
              id="project-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="במה עוסק הפרויקט — יעזור לזהות אותו ברשימה."
              rows={3}
              maxLength={500}
              className="w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-500/15"
            />

            <div className="flex items-center justify-start gap-2 mt-5">
              <button
                type="submit"
                disabled={submitting}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-500 disabled:bg-primary-300 text-white text-sm font-medium press disabled:active:scale-100"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                {submitting ? 'יוצר…' : 'צור פרויקט'}
              </button>
              <button
                type="button"
                onClick={closeDialog}
                disabled={submitting}
                className="px-4 py-2 rounded-lg text-sm text-gray-600 hover:bg-gray-100 transition-colors"
              >
                ביטול
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default ProjectsIndexPage;
